import { memo, useMemo, useCallback } from 'react';
import { Table, Spin, Tooltip } from 'antd';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import clsx from 'clsx';
import MainStyles from 'assets/styles/main.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { calculateHeaderCellWidth } from 'helpers/salaryHelpers/rewards';
import { DownloadOutlined } from '@ant-design/icons';
import { STATUS_LOADING } from 'const';
import {
  REWARD_SUFFIXES_BY_TYPES,
  REWARDS_GENERAL_TAB,
} from 'const/SalaryRewardsPage';
import { REWARDS_PAGE } from 'const/translations/SalaryRewardsPage';
import { selectUserCurrentCompany } from 'modules/loggedUserInfo/selectors';
import {
  setSalaryModalVisibility,
  setSalaryModalEmployee,
} from 'modules/SalaryModule/SalaryModal/actions';
import {
  selectEmployeeTransformRewards,
  selectFamilyRewardsTableColumns,
  selectEmployeeRewardsMeta,
  selectRewardsTableStatus,
  selectEmployeesForReward,
} from 'modules/SalaryModule/RewardsTable/selectors';
import {
  selectExcoInfoModalDataLastDeclarationPeriod,
  selectIsPendingChangesLoading,
} from 'modules/SalaryModule/SalaryModal/selectors';
import { selectChosenFamilyTabId } from 'modules/SalaryModule/Rewards/selectors';

import LeavingQueue from 'assets/icons/leaving-queue.svg?react';

import { NoRewardsDataFound } from '../NoRewardsDataFound';
import { ColumnTitle } from './ColumnTitle';
import { EditableCell } from './EditableCell';
import Styles from './RewardsTable.module.css';

const propTypes = {
  setEmployeeRewardData: PropTypes.func,
  setEmployeeIdXlsx: PropTypes.func,

  currentPage: PropTypes.any,
  setCurrentPage: PropTypes.any,

  // selectedBranch: PropTypes.any,
  activeMonth: PropTypes.object,
};

export const REWARDS_TABLE_ITEM_PER_PAGE = 10;

/**
 * @param setEmployeeIdCsv {function} - sets employee`s id whose csv file is going to be downloaded
 * */
export const RewardsTable = memo(
  ({
    setEmployeeRewardData,
    setEmployeeIdXlsx,
    currentPage,
    setCurrentPage,
    // selectedBranch,
    activeMonth,
  }) => {
    const dispatch = useDispatch();

    const employeesRewards = useSelector(selectEmployeeTransformRewards);
    const employees = useSelector(selectEmployeesForReward);
    const employeesRewardsMeta = useSelector(selectEmployeeRewardsMeta);
    const familyByBranchColumns = useSelector(selectFamilyRewardsTableColumns);
    const { managedSilae } = useSelector(selectUserCurrentCompany);
    const lastDeclarationPeriod = useSelector(
      selectExcoInfoModalDataLastDeclarationPeriod
    );
    const isPendingChangesLoading = useSelector(selectIsPendingChangesLoading);

    const activeTabId = useSelector(selectChosenFamilyTabId);

    const loadingStatus = useSelector(selectRewardsTableStatus);
    const isLoading = [STATUS_LOADING].includes(loadingStatus);

    const createShowSendInfoModalHandler = useCallback(
      (id, name) => () => {
        dispatch(setSalaryModalVisibility(true));
        dispatch(setSalaryModalEmployee({ id, name }));
      },
      [dispatch]
    );

    const setEmployeeIdXlsxHandler = useCallback(
      (e) => {
        const { id } = e.currentTarget.dataset;
        setEmployeeIdXlsx(id);
      },
      [setEmployeeIdXlsx]
    );

    const isTableEmpty = useMemo(
      () => employeesRewards.length === 0 || familyByBranchColumns.length === 0,
      [familyByBranchColumns, employeesRewards]
    );

    const pagination = {
      current: currentPage,
      total: employeesRewardsMeta?.total
        ? Number(employeesRewardsMeta?.total) ||
          employeesRewards / REWARDS_TABLE_ITEM_PER_PAGE
        : employees.length,
      pageSize: REWARDS_TABLE_ITEM_PER_PAGE,
      hideOnSinglePage: true,
      onChange: (page) => setCurrentPage(page),
      showSizeChanger: false,
    };

    const isTableDisabled = useMemo(
      () =>
        lastDeclarationPeriod?.year &&
        lastDeclarationPeriod?.month &&
        moment(
          `${lastDeclarationPeriod.year}.${lastDeclarationPeriod.month}`,
          'YYYY.MM'
        )
          .endOf('month')
          .isAfter(moment(activeMonth)),
      [activeMonth, lastDeclarationPeriod]
    );

    const filteredFamilyByBranchColumns = familyByBranchColumns.filter(
      ({ id }) =>
        activeTabId !== REWARDS_GENERAL_TAB.ID ||
        employeesRewards.some((employee) => Boolean(employee[id]))
    );

    const branchColumns = filteredFamilyByBranchColumns.map(
      ({ name, id, type }) => ({
        title: <ColumnTitle title={name} />,
        width: calculateHeaderCellWidth(name),
        className: Styles.editableCell,
        dataIndex: id,
        key: id,
        render: (value, record) => (
          <EditableCell
            value={value}
            rewardId={id}
            employeeId={record.id}
            handleSave={setEmployeeRewardData}
            suffix={!managedSilae && REWARD_SUFFIXES_BY_TYPES[type]}
            disabled={
              isTableDisabled ||
              (record.lastDeclarationPeriod &&
                moment(
                  `${record.lastDeclarationPeriod.year}-${record.lastDeclarationPeriod.month}`,
                  'YYYY-MM'
                )
                  .endOf('month')
                  .isAfter(moment(activeMonth)))
            }
          />
        ),
      })
    );

    const emptyColumn = {
      title: '',
      dataIndex: 'id',
      key: 'empty',
      render: () => null,
    };

    const branchColumnsWithEmptyCol = (filteredFamilyByBranchColumns.length >
      0 &&
      filteredFamilyByBranchColumns.length <= 4 && [
        ...branchColumns,
        emptyColumn,
      ]) ||
      (filteredFamilyByBranchColumns.length > 4 && branchColumns) || [
        emptyColumn,
      ];

    const columns = useMemo(
      () => [
        {
          title: REWARDS_PAGE.TABLE_NAME_COLUMN,
          width: 180,
          dataIndex: 'name',
          key: 'name',
          fixed: 'left',
          sorter: (a, b) => a.name.localeCompare(b.name),
          defaultSortOrder: 'ascend',
          sortDirections: ['descend', 'ascend'],
        },

        ...branchColumnsWithEmptyCol,
        {
          title: REWARDS_PAGE.LEAVING_EMPLOYEE,
          dataIndex: 'id',
          key: 'actions-leave',
          width: 140,
          render: (id, record) => (
            <Tooltip
              placement="topLeft"
              arrow={{ pointAtCenter: true }}
              title={REWARDS_PAGE.LEAVING_EMPLOYEE_TOOLTIP}
            >
              <LeavingQueue
                onClick={createShowSendInfoModalHandler(id, record.name)}
                data-id={id}
                className={Styles.leavingIcon}
              />
            </Tooltip>
          ),
        },
        {
          title: '',
          dataIndex: 'id',
          key: 'actions',
          width: 48,
          render: (id) => (
            <DownloadOutlined
              onClick={setEmployeeIdXlsxHandler}
              data-id={id}
              className={Styles.Icon}
            />
          ),
        },
      ],
      [
        filteredFamilyByBranchColumns,
        setEmployeeRewardData,
        managedSilae,
        isTableDisabled,
        activeMonth,
        createShowSendInfoModalHandler,
        setEmployeeIdXlsxHandler,
      ]
    );

    if (isLoading) {
      return (
        <div className={Styles.loaderWrap}>
          <Spin />
        </div>
      );
    }

    if (!employeesRewards) {
      return <NoRewardsDataFound />;
    }

    return (
      <Table
        bordered
        className={clsx(
          MainStyles.table,
          MainStyles.tableCustomRow,
          Styles.table,
          {
            [Styles.overflowHidden]: isTableEmpty,
          }
        )}
        dataSource={employeesRewards}
        columns={columns}
        pagination={pagination}
        scroll={{ x: 1130 }}
        rowKey="id"
        loading={isPendingChangesLoading}
      />
    );
  }
);

RewardsTable.propTypes = propTypes;
